import { useState } from 'react'
import { SUPERMARKETS, formatPrice } from '@utils/constants'
import { getPremiumComparison } from '@utils/premiumMatcher'
import { useOfferStore, selectAHCatalogProducts, selectRivalries } from '@store/offerStore'
import { SupermarketBadge } from './SupermarketBadge'
import styles from './OfferCard.module.css'

/** @import { Offer, Rivalry, PremiumComparison, SupermarketMeta } from '../types.js' */

const CATEGORY_EMOJI = {
  groente: '🥦',
  fruit: '🍎',
}

const SOURCE_LABELS = {
  'flyer-vision': '👁 vision',
  'flyer-keywords': '🔤 keywords',
  'flyer-links': '🔗 links',
}

/**
 * Build a Cloudflare Image Resizing URL.
 * `/cdn-cgi/image/` transforms the response from the origin at the edge —
 * serves WebP/AVIF when the browser supports it, at the exact width needed.
 */
function cfImage(path, width) {
  return `/cdn-cgi/image/width=${width},format=auto,fit=scale-down,quality=85/${path.replace(/^\//, '')}`
}

/** Build srcSet entries for an illustration at 1x/2x/3x density. */
function illustrationSrcSet(url) {
  return `${cfImage(url, 120)} 1x, ${cfImage(url, 240)} 2x, ${cfImage(url, 360)} 3x`
}

export function isOfferIllustrated(offer) {
  return Boolean(offer?.illustrationUrl && offer.illustrationUrl.startsWith('/'))
}

function OfferImage({ offer, emoji, hasCrop, cropStyle }) {
  if (isOfferIllustrated(offer)) {
    return (
      <img
        src={offer.illustrationUrl}
        alt={offer.productName}
        loading="lazy"
        decoding="async"
        className={`${styles.componentOfferImage} ${styles.illustration}`}
      />
    )
  }
  if (offer.imageUrl) {
    return <img src={offer.imageUrl} alt={offer.productName} loading="lazy" className={`${styles.componentOfferImage} ${hasCrop ? styles.imageCropped : ''}`} style={cropStyle} />
  }
  return <span role="img" aria-hidden="true" className={styles.componentOfferImage}>{emoji}</span>
}

function getCropStyle(offer) {
  const hasCrop = offer.cropTop !== null && offer.cropTop !== undefined && offer.cropLeft !== null && offer.cropLeft !== undefined
  return hasCrop ? {
    objectFit: 'none',
    objectPosition: `${offer.cropLeft}% ${offer.cropTop}%`,
    transform: 'scale(2)',
  } : undefined
}

function CardImage({ offer }) {
  const emoji = CATEGORY_EMOJI[offer.category] || '🌿'
  const isFlyer = offer.source?.startsWith('flyer-')
  const sourceLabel = SOURCE_LABELS[offer.source]

  const cropStyle = getCropStyle(offer)
  const hasCrop = cropStyle !== undefined
  const image = <OfferImage {...{ offer, emoji, hasCrop, cropStyle }} />

  const flyerBadges = isFlyer && (
    <FlyerDebugBadges
      {...{ sourceLabel, offer }}
    />
  )

  return (
    <div className={styles.componentCardImage}>
      {image}
      {flyerBadges}
    </div>
  )
}

function FlyerDebugBadges({ sourceLabel, offer }) {
  const sourceBadge = sourceLabel && (
    <div className={styles.sourceBadge}>
      {sourceLabel}
    </div>
  )
  const debugBtn = offer.pageImageUrl && (
    <DebugButton {...{ offer }} />
  )

  return (
    <>
      {sourceBadge}
      {debugBtn}
    </>
  )
}

function DebugButton({ offer }) {
  const [showDebug, setShowDebug] = useState(false)

  function handleClick(e) {
    e.preventDefault()
    e.stopPropagation()
    setShowDebug(true)
  }

  const overlay = showDebug && (
    <DebugOverlay
      {...{ offer }}
      onClose={() => setShowDebug(false)}
    />
  )

  return (
    <div className={styles.componentDebugButton}>
      <button
        className={styles.debugTrigger}
        onClick={handleClick}
        title="Bekijk bron pagina"
        type="button"
      >
        🔍
      </button>
      {overlay}
    </div>
  )
}

function DebugOverlay({ offer, onClose }) {
  const hasCrop = offer.cropTop !== null && offer.cropTop !== undefined && offer.cropLeft !== null && offer.cropLeft !== undefined
  const sourceLabel = SOURCE_LABELS[offer.source]

  const cropStyle = {
    objectFit: 'none',
    objectPosition: `${offer.cropLeft}% ${offer.cropTop}%`,
    transform: 'scale(2.5)',
  }

  const cropSection = hasCrop && (
    <DebugCropSection
      {...{ offer, cropStyle }}
    />
  )

  return (
    <div
      className={styles.component_rootDebugOverlay}
      onClick={onClose}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
      role="presentation"
    >
      <div
        className={styles.overlayContent}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="presentation"
      >
        <DebugOverlayHeader
          productName={offer.productName}
          {...{ sourceLabel, onClose }}
        />
        <img
          className={styles.overlayPageImage}
          src={offer.pageImageUrl}
          alt={`Folderpagina bron voor ${offer.productName}`}
        />
        {cropSection}
      </div>
    </div>
  )
}

function DebugCropSection({ offer, cropStyle }) {
  return (
    <div className={styles.componentDebugCropSection}>
      <span className={styles.overlayCropLabel}>
        Gedetecteerd ({offer.cropLeft}%, {offer.cropTop}%):
      </span>
      <div className={styles.overlayCropViewport}>
        <img
          className={styles.overlayCropImage}
          src={offer.pageImageUrl}
          alt={`Crop voor ${offer.productName}`}
          style={cropStyle}
        />
      </div>
    </div>
  )
}

function DebugOverlayHeader({ productName, sourceLabel, onClose }) {
  const titleSuffix = sourceLabel
    ? <span className={styles.overlaySource}> ({sourceLabel})</span>
    : null

  return (
    <div className={styles.componentDebugOverlayHeader}>
      <span className={styles.overlayTitle}>
        📄 Bron: {productName}
        {titleSuffix}
      </span>
      <button className={styles.overlayClose} onClick={onClose} type="button">✕</button>
    </div>
  )
}

/** @param {{ offer: Offer }} props */
export function OfferCard({ offer }) {
  const meta = SUPERMARKETS[offer.supermarket]
  const isIllustrated = isOfferIllustrated(offer)
  const cardClass = isIllustrated
    ? `${styles.component} ${styles.illustrated}`
    : styles.component

  return (
    <a
      className={cardClass}
      href={offer.sourceUrl}
      target="_blank"
      rel="noopener noreferrer"
      title={`Bekijk bij ${meta?.label || offer.supermarket}`}
      data-supermarket={offer.supermarket}
    >
      <div className={styles.mainRow}>
        <CardImage {...{ offer }} />
        
        <div className={styles.contentSection}>
          <div className={styles.metaBadgeRow}>
            <SupermarketBadge supermarket={offer.supermarket} size="xs" />
            <span className={styles.validityDate}>
              t/m {formatDate(offer.validUntil)}
            </span>
          </div>
          
          <h3 className={styles.productName}>{offer.productName}</h3>
          
          <div className={styles.productSpecs}>
            <span className={styles.unitSize}>{offer.unit}</span>
            {offer.pricePerKg != null && (
              <span className={styles.pricePerKg}> · €{offer.pricePerKg.toFixed(2)}/kg</span>
            )}
          </div>
        </div>

        <div className={styles.pricingSection}>
          {offer.discount && (
            <span className={styles.discountTag}>{offer.discount}</span>
          )}
          <div className={styles.priceContainer}>
            {offer.originalPrice && offer.originalPrice > offer.offerPrice && (
              <span className={styles.priceOriginal}>
                {formatPrice(offer.originalPrice)}
              </span>
            )}
            <span className={styles.priceCurrent}>
              {formatPrice(offer.offerPrice)}
            </span>
          </div>
        </div>
      </div>
      
      <PremiumHint {...{ offer }} />
      <RivalryHint {...{ offer }} />
    </a>
  )
}

function PremiumHint({ offer }) {
  const hasAHPremium = useOfferStore(s => s.hasAHPremium)
  const catalogProducts = useOfferStore(selectAHCatalogProducts)

  if (!hasAHPremium || !catalogProducts) return null

  const comparison = getPremiumComparison(offer, catalogProducts)
  if (!comparison || comparison.type !== 'premium-cheaper') return null

  if (offer.supermarket !== 'ah') {
    return (
      <div className={`${styles.componentPremiumHint} ${styles.premiumCheaper}`}>
        <span>💎</span>
        Bij AH met Premium: {formatPrice(comparison.premiumPrice)}
      </div>
    )
  }

  return (
    <div className={`${styles.componentPremiumHint} ${styles.premiumWarning}`}>
      <span>💎</span>
      Meer korting met Premium ({formatPrice(comparison.premiumPrice)})
    </div>
  )
}

function RivalryHint({ offer }) {
  const rivalry = useOfferStore(s => selectRivalries(s).get(offer.id))

  if (!rivalry) return null

  const rivalMeta = SUPERMARKETS[rivalry.rivalSupermarket]
  const rivalLabel = rivalMeta?.shortLabel || rivalry.rivalSupermarket

  // When units differ (e.g. 200g vs 250g), show per-kg context
  const unitContext = rivalry.unitsDiffer
    ? ` (${formatPrice(rivalry.rivalPerUnit)}/kg)`
    : ''

  const savingBadge = rivalry.savingPercent > 0 && (
    <span className={styles.rivalrySaving}>
      −{rivalry.savingPercent}%
    </span>
  )

  const rivalCheaperAbsolute = rivalry.rivalPrice < offer.offerPrice

  const content = rivalry.unitsDiffer && !rivalCheaperAbsolute ? (
    <>
      Goedkoper per kg bij {rivalLabel}: {formatPrice(rivalry.rivalPerUnit)}/kg
      {savingBadge}
    </>
  ) : (
    <>
      Goedkoper bij {rivalLabel}: {formatPrice(rivalry.rivalPrice)}{unitContext}
      {savingBadge}
    </>
  )

  if (rivalry.rivalSourceUrl) {
    return (
      <span
        role="link"
        tabIndex={0}
        className={`${styles.componentRivalryHint} ${styles.rivalryCheaper} ${styles.rivalryLink}`}
        title={`Bekijk bij ${rivalLabel}`}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(rivalry.rivalSourceUrl, '_blank', 'noopener,noreferrer') }}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); window.open(rivalry.rivalSourceUrl, '_blank', 'noopener,noreferrer') } }}
      >
        {content}
      </span>
    )
  }

  return (
    <div className={`${styles.componentRivalryHint} ${styles.rivalryCheaper}`}>
      {content}
    </div>
  )
}

function formatDate(isoDate) {
  if (!isoDate) return ''
  const d = new Date(isoDate)
  return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })
}
